import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import CreatorApplicationLayout from "../../../layouts/CreatorApplicationLayout";
import styles from "./CreatorApplication.module.css";
import Stepper from "./components/Stepper";
import Step1BasicInfo from "./steps/Step1BasicInfo";
import Step2EmailVerification from "./steps/Step2EmailVerification";
import Step3PhoneVerification from "./steps/Step3PhoneVerification";
import Step4CreatorProfile from "./steps/Step4CreatorProfile";
import Step4_1MoreAboutYou from "./steps/Step4_1MoreAboutYou";
import Step4_2LifestyleProfile from "./steps/Step4_2LifestyleProfile";
import VerificationPage from "./steps/VerificationPage";
import CreatorMonetizationStep from "./steps/CreatorMonetizationStep";
import StepMonetizationControls from "./steps/StepMonetizationControls";
import { clearTokens } from "../../../utils/tokens";
import { queryClient } from "../../../query/queryClient";
import { logoutUser, setUser } from "../../../redux/modules/userSlice";
import { editProfileDataAPI } from "../../../api/userApi";

export default function CreatorApplication() {
  const historyRouter = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<{
    step?: number;
    email?: string;
    autoSendOtp?: boolean;
    phoneNumber?: string;
  }>();

  const getMinAllowedStep = () => {
    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    if (!u) return 0;
    const isEmailDone = u.emailVerified === true || u.validated === true;
    const isPhoneDone = u.phoneVerified === true || Boolean(u.phoneNumber);
    const isProfileDone = u.profileCompletion === true || Boolean(u.aboutMe);

    if (isProfileDone) return 3;
    if (isPhoneDone) return 3;
    if (isEmailDone) return 2;
    if (u.email) return 1;
    return 0;
  };

  const getInitialStep = () => {
    if (typeof location.state?.step === "number") {
      return location.state.step;
    }
    const params = new URLSearchParams(location.search);
    const stepParam = params.get("step");
    if (stepParam !== null) {
      const parsed = parseInt(stepParam, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 8) {
        return parsed;
      }
    }

    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    if (!u) return 0;

    const isEmailDone = u.emailVerified === true || u.validated === true;
    const isPhoneDone = u.phoneVerified === true || Boolean(u.phoneNumber);
    const isProfileDone = u.profileCompletion === true || Boolean(u.aboutMe);

    if (!isEmailDone) return 1;
    if (!isPhoneDone) return 2;
    if (!isProfileDone) return 3;
    return 6;
  };

  const minAllowedStep = getMinAllowedStep();
  const rawInitialStep = getInitialStep();
  const initialStep = Math.max(rawInitialStep, minAllowedStep);

  const [activeStep, setActiveStep] = useState(initialStep);
  const [history, setHistory] = useState<number[]>([initialStep]);

  // If user has already completed all onboarding steps, redirect to home
  useEffect(() => {
    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    const isEmailOk = u?.emailVerified === true || u?.validated === true;
    const isPhoneOk = u?.phoneVerified === true || Boolean(u?.phoneNumber);
    const isProfileOk = u?.profileCompletion === true || Boolean(u?.aboutMe) || u?.isOnboarding === true;

    if (isEmailOk && isPhoneOk && isProfileOk && u?.isOnboarding === true) {
      console.log("[CreatorApplication] All steps verified and isOnboarding=true -> Redirecting to home '/'");
      historyRouter.replace("/");
    }
  }, [historyRouter]);

  // Guard: clamp activeStep so user can never access steps already verified
  useEffect(() => {
    const minStep = getMinAllowedStep();
    if (activeStep < minStep) {
      console.warn(`[CreatorApplication:ClampTriggered] activeStep (${activeStep}) < minStep (${minStep}). Clamping to minStep ${minStep}.`);
      setActiveStep(minStep);
      setHistory([minStep]);
    }
  }, [activeStep]);

  const [registeredEmail, setRegisteredEmail] = useState(() => {
    if (location.state?.email) return location.state.email;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        if (u.email) return u.email;
      }
    } catch { }
    return "";
  });

  const [autoSendOtp, setAutoSendOtp] = useState<boolean>(() => {
    if (location.state?.autoSendOtp !== undefined) return location.state.autoSendOtp;
    return initialStep === 1;
  });

  const [registeredPhone, setRegisteredPhone] = useState(() => {
    if (location.state?.phoneNumber) return location.state.phoneNumber;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        if (u.phoneNumber) return u.phoneNumber;
      }
    } catch { }
    return "";
  });

  const [creatorProfileData, setCreatorProfileData] = useState<Record<string, any>>({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeStep]);

  const handleCompleteOnboarding = async (targetRoute: string, targetState?: any) => {
    try {
      await editProfileDataAPI({
        isOnboarding: true,
      });
    } catch (err) {
      console.warn("Could not mark onboarding complete on server:", err);
    }

    try {
      const stored = localStorage.getItem("user");
      const currentUser = stored ? JSON.parse(stored) : {};
      const updatedUser = {
        ...currentUser,
        isOnboarding: true,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      dispatch(setUser(updatedUser));
    } catch (err) {
      console.warn("Error updating user state:", err);
    }

    if (targetState) {
      historyRouter.push({
        pathname: targetRoute,
        state: targetState,
      });
    } else {
      historyRouter.push(targetRoute);
    }
  };

  const handleNext = (param?: number | any) => {
    if (param && typeof param === "object") {
      if (param.email) {
        setRegisteredEmail(param.email);
      }
      if (param.phoneNumber) {
        setRegisteredPhone(param.phoneNumber);
      }
      setCreatorProfileData((prev) => ({
        ...prev,
        ...param,
      }));
    }
    if (activeStep === 8) {
      handleCompleteOnboarding("/");
      return;
    }
    const inc = typeof param === "number" ? param : 1;
    const nextStep = Math.min(activeStep + inc, 8);
    setActiveStep(nextStep);
    setHistory((h) => [...h, nextStep]);
  };

  const handleBack = () => {
    const minStep = getMinAllowedStep();
    if (activeStep <= minStep) {
      console.warn(`[CreatorApplication:handleBack] Cannot go back past minStep (${minStep}). Blocked.`);
      return;
    }
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const targetStep = newHistory[newHistory.length - 1];
      if (targetStep >= minStep) {
        setActiveStep(targetStep);
        setHistory(newHistory);
        return;
      }
    }
    if (activeStep - 1 >= minStep) {
      setActiveStep(activeStep - 1);
      setHistory([activeStep - 1]);
    } else {
      historyRouter.push("/login");
    }
  };

  const currentMinStep = getMinAllowedStep();

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <Step1BasicInfo onNext={handleNext} />;
      case 1:
        return (
          <Step2EmailVerification
            onNext={handleNext}
            onBack={handleBack}
            email={registeredEmail}
            autoSendOtp={autoSendOtp}
            canGoBack={currentMinStep < 1}
          />
        );
      case 2:
        return (
          <Step3PhoneVerification
            onNext={handleNext}
            onBack={handleBack}
            canGoBack={currentMinStep < 2}
          />
        );
      case 3:
        return (
          <Step4CreatorProfile
            onNext={handleNext}
            onBack={handleBack}
            savedData={creatorProfileData}
            canGoBack={currentMinStep < 3}
          />
        );
      case 4:
        return (
          <Step4_1MoreAboutYou
            onNext={handleNext}
            onBack={handleBack}
            savedData={creatorProfileData}
            canGoBack={currentMinStep < 4}
          />
        );
      case 5:
        return (
          <Step4_2LifestyleProfile
            onNext={handleNext}
            onBack={handleBack}
            profileData={creatorProfileData}
            canGoBack={currentMinStep < 5}
          />
        );
      case 6:
        return (
          <VerificationPage
            onNext={handleNext}
            onBack={handleBack}
            canGoBack={currentMinStep < 6}
          />
        );
      case 7:
        return (
          <CreatorMonetizationStep
            onContinue={() => handleNext()}
            onSkip={() => handleCompleteOnboarding("/")}
            onBack={handleBack}
          />
        );
      case 8:
        return (
          <StepMonetizationControls
            onNext={() => handleCompleteOnboarding("/")}
            onBack={handleBack}
            onSkip={() => handleCompleteOnboarding("/")}
          />
        );
      default:
        return null;
    }
  };

  const isEarlyStep = activeStep === 0 || activeStep === 1;

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isEarlyStep) {
      e.preventDefault();
      clearTokens();
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        localStorage.removeItem("role");
        localStorage.removeItem("hasRegisteredOrLoggedIn");
      } catch (err) {
        console.warn("Error clearing localStorage:", err);
      }
      queryClient.clear();
      dispatch(logoutUser());
      historyRouter.push("/");
    }
  };

  return (
    <CreatorApplicationLayout
      logoTarget={isEarlyStep ? "/" : "/apply-as-creator"}
      onLogoClick={handleLogoClick}
    >
      <div className={activeStep >= 7 ? styles.monetizationContainer : styles.container}>

        {/* Stepper Component (hidden for Monetization Intro step) */}
        {activeStep < 7 && <Stepper activeStep={activeStep} />}

        {/* Dynamic Form Step */}
        {renderStep()}

      </div>
    </CreatorApplicationLayout>
  );
}
